import React, { useState, useEffect, useCallback } from 'react';
import { marketingApi, Asset } from '../services/marketingApi';
import { assetTypeToMediaType, MediaType } from '../types/asset';

interface AssetPickerProps {
  isOpen: boolean;
  onSelect: (asset: Asset, mediaType: MediaType, caption?: string) => void;
  onClose: () => void;
}

type TabType = 'image' | 'video' | 'pdf';

const AssetPicker: React.FC<AssetPickerProps> = ({ isOpen, onSelect, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('image');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [caption, setCaption] = useState('');

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await marketingApi.getAssets(activeTab);
      setAssets(data);
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError('Failed to load assets.');
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
    }
  }, [isOpen, fetchAssets]);

  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setCaption('');
  };

  const handleSend = () => {
    if (!selectedAsset) return;
    const mediaType = assetTypeToMediaType(selectedAsset.type);
    onSelect(selectedAsset, mediaType, caption.trim() || undefined);
    setSelectedAsset(null);
    setCaption('');
  };

  const handleBackToGrid = () => {
    setSelectedAsset(null);
    setCaption('');
  };

  const handleClose = () => {
    setSelectedAsset(null);
    setCaption('');
    onClose();
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'image', label: 'Images' },
    { id: 'video', label: 'Videos' },
    { id: 'pdf', label: 'Documents' },
  ];

  if (!isOpen) return null;

  // Preview mode - show selected asset with caption input
  if (selectedAsset) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-warm-white rounded-xl shadow-sketch-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-sketch">
            <button
              onClick={handleBackToGrid}
              className="p-1 text-graphite hover:text-charcoal hover:bg-cream rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-lg font-semibold text-charcoal flex-1 text-center">Add Caption</h3>
            <button
              onClick={handleClose}
              className="p-1 text-graphite hover:text-charcoal hover:bg-cream rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Asset Preview */}
          <div className="flex-1 overflow-y-auto p-4 bg-cream">
            <div className="bg-warm-white rounded-lg overflow-hidden shadow-sketch">
              {/* Media Preview */}
              <div className="aspect-video bg-cream flex items-center justify-center">
                {selectedAsset.type === 'image' && selectedAsset.url ? (
                  <img
                    src={selectedAsset.url}
                    alt={selectedAsset.name}
                    className="w-full h-full object-contain"
                  />
                ) : selectedAsset.type === 'video' ? (
                  <div className="flex flex-col items-center text-graphite/70">
                    <svg className="w-16 h-16 mb-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <p className="text-sm">{selectedAsset.name}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-graphite/70">
                    <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">{selectedAsset.name}</p>
                  </div>
                )}
              </div>

              {/* Asset Name */}
              <div className="px-3 py-2 border-t border-sketch-soft">
                <p className="text-sm text-graphite truncate">{selectedAsset.name}</p>
              </div>
            </div>
          </div>

          {/* Caption Input & Send */}
          <div className="p-4 border-t border-sketch bg-warm-white">
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption... (optional)"
              rows={2}
              className="w-full px-3 py-2 text-sm border border-sketch rounded-lg focus:outline-none focus:border-[#00a884] resize-none mb-3"
            />
            <button
              onClick={handleSend}
              className="w-full py-3 bg-[#00a884] text-warm-white font-medium rounded-lg hover:bg-[#008f72] transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"/>
              </svg>
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grid mode - show asset selection
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-warm-white rounded-xl shadow-sketch-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-sketch">
          <h3 className="text-lg font-semibold text-charcoal">Select Attachment</h3>
          <button
            onClick={handleClose}
            className="p-1 text-graphite hover:text-charcoal hover:bg-cream rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-sketch">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-[#00a884] border-b-2 border-[#00a884]'
                  : 'text-graphite hover:text-charcoal'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-sketch-soft">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-graphite/70"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-sketch rounded-lg focus:outline-none focus:border-[#00a884]"
            />
          </div>
        </div>

        {/* Assets Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 border-2 border-[#00a884] border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm text-graphite">Loading assets...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-brick mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-brick mb-2">{error}</p>
                <button
                  onClick={fetchAssets}
                  className="text-sm text-[#00a884] hover:underline"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-graphite/50 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm text-graphite">
                  {searchQuery ? 'No matching assets found' : 'No assets available'}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {filteredAssets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => handleAssetClick(asset)}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-sketch hover:border-[#00a884] hover:shadow-md transition-all"
                >
                  {/* Thumbnail */}
                  {asset.type === 'image' && asset.url ? (
                    <img
                      src={asset.url}
                      alt={asset.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '';
                        (e.target as HTMLImageElement).className = 'hidden';
                      }}
                    />
                  ) : asset.type === 'video' ? (
                    <div className="w-full h-full bg-cream flex items-center justify-center">
                      <svg className="w-10 h-10 text-graphite/70" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-cream flex items-center justify-center">
                      <svg className="w-10 h-10 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}

                  {/* Name overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <p className="text-xs text-warm-white truncate">{asset.name}</p>
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-[#00a884]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-warm-white rounded-full p-2 shadow-lg">
                      <svg className="w-5 h-5 text-[#00a884]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetPicker;
